/**
 * CommissioningView — full commissioning workflow panel.
 * Shown when gateway lifecycle_status = 'commissioning'.
 * Left: discovered devices. Right: commissioning checklist.
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, X, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { GatewayBusDeviceList } from './GatewayBusDeviceList';
import { useGatewayDeviceConfigs } from '@/hooks/useGatewayDeviceConfigs';
import { useGatewayCommissioning } from '@/hooks/useGatewayCommissioning';
import { useGatewayCommands } from '@/hooks/useGatewayCommands';
import type { GatewayDevice, CommissioningCheckResult } from '@/types/gateway';
import { LIFECYCLE_STATUS_CONFIG, getWifiQuality } from '@/types/gateway';
import { toast } from 'sonner';

interface CommissioningViewProps {
  device: GatewayDevice;
}

export function CommissioningView({ device }: CommissioningViewProps) {
  const { configs, isLoading: configsLoading, refetch: refetchConfigs } = useGatewayDeviceConfigs(device.id);
  const commissioning = useGatewayCommissioning(device, configs);
  const { issueCommand, isIssuing } = useGatewayCommands(device.id);

  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [expectedCount, setExpectedCount] = useState<number | null>(null);

  const effectiveExpectedCount = expectedCount ?? configs.length;

  const handleOverride = (checkName: string, note: string) => {
    setOverrides((prev) => ({ ...prev, [checkName]: note }));
  };

  const checksWithOverrides: CommissioningCheckResult[] = useMemo(
    () =>
      commissioning.checks.map((c) => ({
        ...c,
        overridden: Boolean(overrides[c.name]),
        override_note: overrides[c.name],
      })),
    [commissioning.checks, overrides]
  );

  const canComplete = useMemo(() => {
    return checksWithOverrides.every(
      (c) => c.passed || c.overridden || (!c.critical && c.overrideable)
    );
  }, [checksWithOverrides]);

  const allCriticalPassed = commissioning.criticalsPassed;

  const handleRescan = async () => {
    try {
      await issueCommand({ commandType: 'diagnostic_request' });
      toast.info('Bus scan requested. Results will appear shortly.');
      setTimeout(() => refetchConfigs(), 5000);
    } catch {
      toast.error('Failed to request bus scan');
    }
  };

  const handleComplete = async () => {
    try {
      const overrideList = Object.entries(overrides)
        .filter(([, note]) => note)
        .map(([check_name, note]) => ({ check_name, note }));

      await commissioning.completeCommissioning({
        overrides: overrideList,
        busDevicesExpected: effectiveExpectedCount,
      });
      toast.success('Commissioning complete. Gateway is now active.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete commissioning');
    }
    setShowCompleteDialog(false);
  };

  const wifiQ = getWifiQuality(device.wifi_rssi);

  return (
    <div className="space-y-4">
      {/* Top banner */}
      <div className="rounded-lg bg-wt-amber-primary/10 border border-wt-amber-primary/20 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-wt-amber-primary flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-wt-amber-primary">Commissioning in Progress</p>
            <div className="flex items-center gap-3 text-xs text-wt-text-secondary mt-0.5">
              <span>Firmware: <span className="font-mono-numeric">{device.firmware_version ?? '--'}</span></span>
              <span>WiFi: <span className={`font-mono-numeric ${wifiQ.color}`}>{device.wifi_rssi ?? '--'} dBm</span></span>
              <Badge variant="outline" className={LIFECYCLE_STATUS_CONFIG[device.lifecycle_status].color}>
                {LIFECYCLE_STATUS_CONFIG[device.lifecycle_status].label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Discovered Devices */}
        <div className="space-y-3">
          <GatewayBusDeviceList configs={configs} isLoading={configsLoading} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleRescan} disabled={isIssuing}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isIssuing ? 'animate-spin' : ''}`} />
              Re-scan Bus
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-wt-text-secondary">Expected devices:</span>
            <Input
              type="number"
              min={0}
              value={effectiveExpectedCount}
              onChange={(e) => setExpectedCount(Number(e.target.value) || 0)}
              className="w-20 h-7 font-mono-numeric text-sm"
            />
          </div>
        </div>

        {/* Right: Commissioning Checklist */}
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-wt-text-primary">
              Commissioning Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {checksWithOverrides.map((check) => (
              <ChecklistItem
                key={check.name}
                check={check}
                onOverride={(note) => handleOverride(check.name, note)}
              />
            ))}

            {commissioning.serverChecksLoading && (
              <div className="flex items-center gap-2 py-2 text-sm text-wt-text-secondary">
                <Loader2 className="w-4 h-4 animate-spin" />
                Awaiting server validation...
              </div>
            )}

            {commissioning.serverChecksError && (
              <p className="text-xs text-wt-red-primary">
                Server checks failed: {commissioning.serverChecksError.message}
              </p>
            )}

            <div className="pt-3">
              <Button
                onClick={() => setShowCompleteDialog(true)}
                disabled={!allCriticalPassed || commissioning.isCompleting}
                className="w-full"
              >
                {commissioning.isCompleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Complete Commissioning'
                )}
              </Button>
              {!allCriticalPassed && (
                <p className="text-xs text-wt-text-secondary mt-1 text-center">
                  All critical checks must pass before completing
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">Complete Commissioning</AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary">
              This will activate the gateway for production monitoring. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete}>Activate Gateway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChecklistItem
// ---------------------------------------------------------------------------

function ChecklistItem({
  check,
  onOverride,
}: {
  check: CommissioningCheckResult;
  onOverride: (note: string) => void;
}) {
  const [showOverride, setShowOverride] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');

  const isOverridden = check.overridden;
  const effectivePassed = check.passed || isOverridden;

  return (
    <div className="flex flex-col gap-1 py-1.5 border-b border-border/50 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          {effectivePassed ? (
            <Check className="w-4 h-4 text-wt-green-primary mt-0.5 flex-shrink-0" />
          ) : (
            <X className={`w-4 h-4 mt-0.5 flex-shrink-0 ${check.critical ? 'text-wt-red-primary' : 'text-wt-amber-primary'}`} />
          )}
          <div>
            <p className="text-sm text-wt-text-primary">{check.name}</p>
            <p className="text-xs text-wt-text-secondary">{check.message}</p>
            {isOverridden && (
              <p className="text-xs text-wt-amber-primary mt-0.5">
                Overridden: {check.override_note}
              </p>
            )}
          </div>
        </div>
        {!check.passed && check.overrideable && !isOverridden && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-6 px-2"
            onClick={() => setShowOverride(!showOverride)}
          >
            Override
          </Button>
        )}
      </div>
      {showOverride && (
        <div className="flex gap-2 pl-6">
          <Input
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
            placeholder="Reason for override (required)"
            className="h-7 text-xs"
          />
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!overrideNote.trim()}
            onClick={() => {
              onOverride(overrideNote.trim());
              setShowOverride(false);
            }}
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  );
}
